//node-modules
import { useTranslation } from "react-i18next";
import {
  Grid,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  styled,
  Stack,
} from "@mui/material";
import { useEffect, useState, useContext, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
//src
import Icon from "../Icon";
import Spacer from "../../components/Spacer";
import { useInView } from "react-intersection-observer";
import { config } from "../../config/config";
import MOCK_IMAGE from "../../../images/mock_images/pointcloud_img2.png";
import { ProjectDetailContext } from "./Interfaces/ProjectDetailInterfaces";
import CloseIcon from "../../../images/icons/neutral_edit/Close.svg";
import ChevronRight from "../../../images/icons/neutral_edit/Chevron Right.svg";
import ChevrontLeft from "../../../images/icons/neutral_edit/Chevron Left.svg";
import { Common } from "../../../common/src";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

const autoPlayTime = 500;
let prevIndex = 0;
let isImageLoad = false;

interface PhotoProps {
  photoURLs: Common.PhotoURL[];
  offset: number;
  setPhotoData: React.Dispatch<
    React.SetStateAction<{ [key: number]: { name: string; data: string } }>
  >;
  photoData: { [key: number]: { name: string; data: string } };
  handleOpen: (offset: number) => void;
  isDialogOpen: boolean;
}

type GenerateOffsetSequence = (
  offset: number,
  maxOffset: number,
  rangeSize: number
) => number[];

const Photo = ({
  photoURLs,
  offset,
  setPhotoData,
  handleOpen,
  photoData,
  isDialogOpen,
}: PhotoProps) => {
  const { ref, inView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    let controller = new AbortController();

    const fetchData = async () => {
      try {
        if (!inView || photoData[offset]) {
          // inViewがfalseまたはデータが既に存在する場合は非同期処理をキャンセル
          controller.abort();
          return;
        }

        const name = photoURLs[offset].name;

        let hostName = config.serverHost.includes("localhost")
          ? "http://localhost:9090/quick3d"
          : config.serverHost;
        const url = `${hostName}/${photoURLs[offset].url}`;

        setPhotoData((prevPaths) => ({
          ...prevPaths,
          [offset]: {
            name: name,
            data: url,
          },
        }));
      } catch (e) {
        console.error(e);
      }
    };

    if (!isDialogOpen) {
      fetchData();
    }

    return () => {
      controller.abort();
      controller = new AbortController();
    };
  }, [inView]);

  const theme = useTheme();

  const customColor = {
    background: theme.palette.base.menu,
    color: theme.palette.base.contrast,
  };

  const className = `photo-${offset}-img`;

  return (
    <div
      ref={ref}
      style={{
        width: "300px",
        height: "200px",
        margin: "10px",
        cursor: "pointer",
        position: "relative",
      }}
    >
      {inView ? (
        <>
          <img
            src={
              photoData[offset]?.data ? `${photoData[offset].data}` : MOCK_IMAGE
            }
            className={className}
            width="100%"
            height="100%"
            onClick={() => handleOpen(offset)}
            alt=""
          />
          {photoData[offset]?.name ? (
            <p className="list-photo-img-title" style={{ ...customColor }}>
              {photoData[offset].name}
            </p>
          ) : null}
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

const Photos = ({ photoURLs }: any) => {
  //ProjectListのアイテムを生成
  const { t } = useTranslation();
  const { projectDetail } = useContext(ProjectDetailContext);

  const [photoNum, setPhotoNum] = useState<number>(0);
  const [photoLoading, setPhotoLoading] = useState<boolean>(false);
  const [photoData, setPhotoData] = useState<{
    [key: number]: { name: string; data: string };
  }>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const photoIds = [...Array(photoNum)].map((_, i) => i);

  // モーダルの画像をフェッチする関数
  const fetchImagesInRange = useCallback(
    async (offset: number) => {
      const generateOffsetSequence: GenerateOffsetSequence = (
        offset,
        maxOffset,
        rangeSize
      ) => {
        const sequence: number[] = [];

        for (let i = offset - rangeSize; i < offset; i++) {
          sequence.push(i < 0 ? maxOffset + i : i);
        }

        for (let i = offset; i <= offset + rangeSize; i++) {
          sequence.push(i >= maxOffset ? i - maxOffset : i);
        }

        return sequence;
      };

      const maxOffset = photoIds.length;
      const rangeSize = 10;

      const result = generateOffsetSequence(offset, maxOffset, rangeSize);

      const fetchImage = async (
        offset: number,
        photoDataArray: { [key: number]: { name: string; data: string } }
      ) => {
        if (!photoData[offset]) {
          try {
            const name = photoURLs[offset].name;

            let hostName = config.serverHost.includes("localhost")
              ? "http://localhost:9090/quick3d"
              : config.serverHost;
            const url = `${hostName}/${photoURLs[offset].url}`;
            photoDataArray[offset] = {
              name: name,
              data: url,
            };
          } catch (e) {
            console.error(e);
          }
        }
      };
      const photoDataArray = photoData;
      result.forEach((offset) => {
        fetchImage(offset, photoDataArray);
      });
      setPhotoData(photoDataArray);
    },
    [photoData, photoIds.length, photoURLs]
  );

  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isPause, setIsPause] = useState(true);
  const autoPlayRef = useRef(null);

  const handleImageLoad = () => {
    isImageLoad = false;
  };

  const autoPlay = useCallback(
    (direction: "prev" | "next") => {
      setIsPause(false);
      setIsAutoPlay(true);

      autoPlayRef.current = setInterval(async () => {
        if (!isImageLoad) {
          let newIndex = prevIndex;
          if (direction === "prev") {
            newIndex = prevIndex === 0 ? photoIds.length - 1 : prevIndex - 1;
          } else {
            newIndex = prevIndex === photoIds.length - 1 ? 0 : prevIndex + 1;
          }
          prevIndex = newIndex;
          await fetchImagesInRange(newIndex);
          isImageLoad = true;
          setCurrentImageIndex(newIndex);
        }
      }, autoPlayTime);
    },
    [fetchImagesInRange, photoIds.length]
  );

  const handlePause = () => {
    setIsPause(true);
    setIsAutoPlay(false);
    window.clearInterval(autoPlayRef.current);
  };

  useEffect(() => {
    return () => {
      window.clearInterval(autoPlayRef.current);
    };
  }, []);

  useEffect(() => {
    const currentImage = new Image();
    currentImage.src = photoData[currentImageIndex]?.data || MOCK_IMAGE;

    // 前後の画像をプリロード
    const nextIndex = currentImageIndex + 1;
    const prevIndex = currentImageIndex - 1;
    for (let i = nextIndex; i < nextIndex + 5; i++) {
      const nextImage = new Image();
      nextImage.src = photoData[i]?.data || MOCK_IMAGE;
    }
    for (let i = prevIndex; i > prevIndex - 5; i--) {
      const prevImage = new Image();
      prevImage.src = photoData[i]?.data || MOCK_IMAGE;
    }
  }, [currentImageIndex]);

  const handleOpen = (offset: number) => {
    prevIndex = offset;
    setIsDialogOpen(true);
    setCurrentImageIndex(offset);
    fetchImagesInRange(offset);
  };

  const handleClose = () => {
    handlePause();
    setIsDialogOpen(false);
  };

  const handleNavigate = async (direction: "prev" | "next") => {
    let newIndex = prevIndex;
    if (direction === "prev") {
      newIndex = prevIndex === 0 ? photoIds.length - 1 : prevIndex - 1;
    } else {
      newIndex = prevIndex === photoIds.length - 1 ? 0 : prevIndex + 1;
    }
    prevIndex = newIndex;
    await fetchImagesInRange(newIndex);
    isImageLoad = true;
    setCurrentImageIndex(newIndex);
  };

  const handleDownload = async () => {
    try {
      const dataUri = photoData[currentImageIndex]?.data;
      const response = await fetch(dataUri);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = photoData[currentImageIndex]?.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setPhotoLoading(true);
        setPhotoNum(photoURLs.length);
        setPhotoLoading(false);
      } catch (e) {
        setPhotoLoading(false);
        console.error(e);
      }
    };

    if (projectDetail.id) {
      fetchData();
    }

    return () => {
      setPhotoLoading(false);
    };
  }, [projectDetail.id, setPhotoNum, setPhotoLoading, photoURLs.length]);

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const PhotoCounts = () =>
    photoIds.length !== 0 && (
      <p
        style={{
          fontSize: 30,
          marginTop: -15,
          paddingLeft: 15,
          ...customColor,
        }}
      >
        {photoIds.length}
        <span style={{ fontSize: 25 }}>{t("PIECES")}</span>
      </p>
    );
  const RightIcon = () => (
    <Icon
      imagePath={ChevronRight}
      color={customColor.color}
      width={50}
      height={50}
    />
  );
  const LeftIcon = () => (
    <Icon
      imagePath={ChevrontLeft}
      color={customColor.color}
      width={50}
      height={50}
    />
  );
  const CustomCloseIcon = () => (
    <Icon imagePath={CloseIcon} color={customColor.color} />
  );
  const CustomDownloadButton = styled(Button)({
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.primary.main,
      boxShadow: "none",
    },
  });

  const listItems = photoIds.map(function (offset, index) {
    return (
      <Photo
        photoURLs={photoURLs}
        offset={offset}
        key={index}
        handleOpen={handleOpen}
        setPhotoData={setPhotoData}
        photoData={photoData}
        isDialogOpen={isDialogOpen}
      />
    );
  });

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <Spacer width={0} height={10} horizontal={true} />
      <PhotoCounts />
      <Box sx={{ flexGrow: 1 }}>
        {photoLoading ? (
          <CircularProgress
            sx={{ position: "absolute", left: "50%", top: "50%" }}
          />
        ) : photoIds.length !== 0 ? (
          <Grid
            container
            spacing={{ xs: 2, md: 3 }}
            columns={{ xs: 4, sm: 8, md: 12 }}
            justifyContent="center"
          >
            {listItems}
          </Grid>
        ) : (
          <div style={{ color: customColor.color }}>{t("NO_PHOTO")}</div>
        )}
      </Box>
      <Dialog
        className="photo-dialog"
        open={isDialogOpen}
        onClose={handleClose}
      >
        <DialogTitle>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <div>
              <span>
                {currentImageIndex + 1}/{photoIds.length}
              </span>
              {photoData[currentImageIndex]?.name
                ? `${photoData[currentImageIndex].name}`
                : null}
            </div>
            <Stack direction="row" gap={4}>
              <Stack direction="row" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => autoPlay("prev")}
                  disabled={isAutoPlay}
                >
                  <PlayArrowIcon sx={{ transform: "rotate(180deg)" }} />
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handlePause}
                  disabled={isPause}
                >
                  <PauseIcon />
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => autoPlay("next")}
                  disabled={isAutoPlay}
                >
                  <PlayArrowIcon />
                </Button>
              </Stack>
              <CustomDownloadButton
                disabled={!photoData[currentImageIndex] || isAutoPlay}
                size="small"
                variant="contained"
                color="primary"
                onClick={() => handleDownload()}
              >
                {t("DOWNLOAD")}
              </CustomDownloadButton>
              <IconButton
                color="inherit"
                onClick={handleClose}
                aria-label="close"
                size="small"
              >
                <CustomCloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent className="photo-dialog-content">
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => handleNavigate("prev")}
            aria-label="previous"
            style={{ fontSize: "1rem" }}
          >
            <LeftIcon />
          </IconButton>
          <img
            src={
              photoData[currentImageIndex]?.data
                ? photoData[currentImageIndex].data
                : MOCK_IMAGE
            }
            alt=""
            onLoad={handleImageLoad}
          />
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => handleNavigate("next")}
            aria-label="next"
            style={{ fontSize: "1rem" }}
          >
            <RightIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Photos;
